import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const INTEGRATIONS = [
  { id: 'quickbooks', name: 'QuickBooks', logo: '📊', color: '#2CA01C', description: 'Sync client financial data, profit & loss, balance sheets, and transactions directly from QuickBooks Online.', features: ['Auto-import transactions', 'Sync chart of accounts', 'Pull P&L and balance sheets', 'Real-time financial data'], live: true },
  { id: 'plaid', name: 'Plaid', logo: '🏦', color: '#0A85EA', description: 'Allow clients to securely connect their bank accounts so you can view transactions and balances in real time.', features: ['Bank account connection', 'Real-time transactions', 'Balance verification', 'Income verification'], live: false },
  { id: 'google_drive', name: 'Google Drive', logo: '📁', color: '#4285F4', description: 'Sync documents between ClearFile and Google Drive. Auto-organize client files into Drive folders.', features: ['Auto-sync uploaded documents', 'Create client folders', 'Two-way file sync', 'Shared drive support'], live: false },
  { id: 'xero', name: 'Xero', logo: '🔵', color: '#1AB4D7', description: 'Connect to Xero to sync accounting data, invoices, payroll, and bank transactions for your clients.', features: ['Sync invoices & bills', 'Import bank transactions', 'Pull payroll data', 'Automated reconciliation'], live: false },
]

function generateApiKey() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let key = 'cf_live_'
  for (let i = 0; i < 32; i++) key += chars[Math.floor(Math.random() * chars.length)]
  return key
}

async function sha256Browser(str) {
  const encoder = new TextEncoder()
  const data = encoder.encode(str)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('')
}

function ApiKeysSection({ userId }) {
  const [keys, setKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyValue, setNewKeyValue] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => { loadKeys() }, [userId])

  async function loadKeys() {
    const { data } = await supabase.from('mcp_api_keys').select('id, name, key_prefix, created_at, last_used_at, is_active').eq('created_by', userId).order('created_at', { ascending: false })
    setKeys(data || [])
    setLoading(false)
  }

  async function generateKey() {
    if (!newKeyName.trim()) return
    setGenerating(true)
    const rawKey = generateApiKey()
    const hash = await sha256Browser(rawKey)
    const prefix = rawKey.substring(0, 12)
    const { error } = await supabase.from('mcp_api_keys').insert({ key_hash: hash, key_prefix: prefix, name: newKeyName.trim(), created_by: userId, is_active: true })
    if (!error) { setNewKeyValue(rawKey); setNewKeyName(''); await loadKeys() }
    setGenerating(false)
  }

  async function revokeKey(id) {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return
    await supabase.from('mcp_api_keys').update({ is_active: false }).eq('id', id)
    await loadKeys()
  }

  function copyKey() {
    navigator.clipboard.writeText(newKeyValue)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 24 }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', background: '#202326', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 28 }}>🤖</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Claude AI API Keys</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>Connect ClearFile to Claude using the Model Context Protocol</div>
        </div>
      </div>
      <div style={{ padding: '24px' }}>
        <div style={{ background: '#F1E8D7', border: '1px solid #CBB17B', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13, color: '#765B2C', lineHeight: 1.6 }}>
          <strong>How to connect:</strong> Generate a key below, then go to <strong>Claude.ai → Settings → Customize → Connectors → Add custom connector</strong> and enter:<br />
          URL: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>https://clearfile.org/api/mcp</code><br />
          Claude will open the ClearFile OAuth screen. Paste this key there to approve the connection.
        </div>

        {newKeyValue && (
          <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 8, padding: '16px', marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#065F46', marginBottom: 8 }}>✅ API Key Generated — Copy it now, it won't be shown again</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <code style={{ flex: 1, background: '#fff', padding: '10px 14px', borderRadius: 6, fontSize: 12, color: '#202326', wordBreak: 'break-all', border: '1px solid #6EE7B7' }}>{newKeyValue}</code>
              <button onClick={copyKey} style={{ flexShrink: 0, padding: '10px 16px', background: copied ? '#065F46' : '#202326', color: '#CBB17B', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#065F46', marginTop: 8 }}>Store this key securely. You cannot view it again after closing this banner.</div>
          </div>
        )}

        <div style={{ background: '#F7F3EB', borderRadius: 10, padding: '16px 18px', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#202326' }}>Generate New API Key</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1.5px solid #DED6CA', fontSize: 13, outline: 'none', fontFamily: "'DM Sans', sans-serif" }} placeholder="Key name (e.g. Claude Personal)" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} onKeyDown={e => e.key === 'Enter' && generateKey()} />
            <button onClick={generateKey} disabled={generating || !newKeyName.trim()} style={{ flexShrink: 0, padding: '10px 18px', background: '#202326', color: '#CBB17B', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer', opacity: generating || !newKeyName.trim() ? 0.6 : 1 }}>
              {generating ? 'Generating…' : '+ Generate Key'}
            </button>
          </div>
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#202326' }}>Active Keys</div>
        {loading ? (
          <div className="skeleton" style={{ height: 60, borderRadius: 8 }} />
        ) : keys.filter(k => k.is_active).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#62615D', fontSize: 13, background: '#F7F3EB', borderRadius: 8 }}>No active API keys. Generate one above to connect Claude.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {keys.filter(k => k.is_active).map(k => (
              <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#F7F3EB', borderRadius: 8, border: '1px solid #DED6CA' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#202326' }}>{k.name}</div>
                  <div style={{ fontSize: 11, color: '#62615D', marginTop: 2 }}>{k.key_prefix}… · Created {new Date(k.created_at).toLocaleDateString()}{k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}`}</div>
                </div>
                <button onClick={() => revokeKey(k.id)} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #F87171', color: '#F87171', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Revoke</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function QuickBooksModal({ clients, onClose }) {
  const [selectedClient, setSelectedClient] = useState('')
  const [connections, setConnections] = useState([])
  const [syncing, setSyncing] = useState(null)
  const [syncResult, setSyncResult] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadConnections()
    const params = new URLSearchParams(window.location.search)
    if (params.get('success') === 'quickbooks') { setSyncResult('QuickBooks connected successfully!'); window.history.replaceState({}, '', window.location.pathname); loadConnections() }
    if (params.get('error')) { setSyncResult('Connection failed: ' + params.get('error')); window.history.replaceState({}, '', window.location.pathname) }
  }, [])

  async function loadConnections() {
    const { data } = await supabase.from('client_integrations').select('*, clients(full_name, business_name)').eq('provider', 'quickbooks')
    setConnections(data || []); setLoading(false)
  }

  function connectQuickBooks() {
    if (!selectedClient) return
    window.location.href = `/.netlify/functions/quickbooks-auth?client_id=${selectedClient}`
  }

  async function syncData(connectionId, clientId, syncType) {
    setSyncing(connectionId + syncType); setSyncResult('')
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session?.access_token) throw new Error('Your session has expired. Please sign in again.')
      const res = await fetch('/.netlify/functions/quickbooks-sync', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ client_id: clientId, sync_type: syncType }) })
      const data = await res.json()
      if (data.error) setSyncResult('Sync failed: ' + data.error)
      else setSyncResult(`✅ Synced ${data.synced || 0} ${syncType} successfully!`)
      loadConnections()
    } catch (err) { setSyncResult('Error: ' + err.message) }
    setSyncing(null)
  }

  async function disconnect(connectionId) {
    await supabase.from('client_integrations').update({ status: 'disconnected' }).eq('id', connectionId)
    loadConnections()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', padding: 0 }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F7F3EB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 28 }}>📊</div>
            <div><h2 style={{ fontSize: 17, margin: 0 }}>QuickBooks Connections</h2><p style={{ fontSize: 12, color: '#62615D', margin: 0 }}>Connect and sync client QuickBooks accounts</p></div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#62615D', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '24px' }}>
          {syncResult && <div style={{ background: syncResult.includes('✅') ? '#D1FAE5' : '#FEE2E2', border: `1px solid ${syncResult.includes('✅') ? '#6EE7B7' : '#F87171'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, fontWeight: 600, color: syncResult.includes('✅') ? '#065F46' : '#991B1B' }}>{syncResult}</div>}
          <div style={{ background: '#F7F3EB', borderRadius: 10, padding: '16px 18px', marginBottom: 24 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#202326' }}>Connect a Client's QuickBooks</h4>
            <div style={{ display: 'flex', gap: 10 }}>
              <select className="input" value={selectedClient} onChange={e => setSelectedClient(e.target.value)} style={{ flex: 1 }}>
                <option value="">Select a client…</option>
                {clients.map(c => (<option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>))}
              </select>
              <button className="btn btn-primary" onClick={connectQuickBooks} disabled={!selectedClient} style={{ flexShrink: 0 }}>🔌 Connect</button>
            </div>
          </div>
          <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#202326' }}>Active Connections</h4>
          {loading ? <div className="skeleton" style={{ height: 60, borderRadius: 8 }} /> : connections.filter(c => c.status === 'connected').length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#62615D', fontSize: 13, background: '#F7F3EB', borderRadius: 8 }}>No QuickBooks connections yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {connections.filter(c => c.status === 'connected').map(conn => (
                <div key={conn.id} className="card" style={{ padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div><div style={{ fontSize: 14, fontWeight: 700 }}>{conn.clients?.business_name || conn.clients?.full_name}</div><div style={{ fontSize: 11, color: '#10B981', fontWeight: 600 }}>● Connected</div>{conn.last_synced_at && <div style={{ fontSize: 11, color: '#62615D', marginTop: 2 }}>Last synced: {new Date(conn.last_synced_at).toLocaleDateString()}</div>}</div>
                    <button onClick={() => disconnect(conn.id)} className="btn btn-ghost" style={{ fontSize: 11, color: '#F87171', padding: '4px 10px' }}>Disconnect</button>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[{ type: 'transactions', label: '💰 Sync Transactions' }, { type: 'profit_loss', label: '📈 P&L Report' }, { type: 'balance_sheet', label: '📋 Balance Sheet' }].map(s => (
                      <button key={s.type} className="btn btn-ghost" style={{ fontSize: 11, flex: 1, justifyContent: 'center' }} onClick={() => syncData(conn.id, conn.client_id, s.type)} disabled={syncing === conn.id + s.type}>{syncing === conn.id + s.type ? 'Syncing…' : s.label}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ComingSoonModal({ integration, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="card animate-fadeUp" style={{ width: '100%', maxWidth: 440, padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{integration.logo}</div>
        <h2 style={{ fontSize: 20, marginBottom: 8 }}>{integration.name} — Coming Soon</h2>
        <p style={{ color: '#62615D', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{integration.description}</p>
        <div style={{ background: '#F1E8D7', border: '1px solid #CBB17B', borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#765B2C' }}>🚀 This integration is being built next. Check back soon!</div>
        <button className="btn btn-primary" onClick={onClose} style={{ width: '100%', justifyContent: 'center' }}>Got It</button>
      </div>
    </div>
  )
}

export default function IntegrationsPage() {
  const { profile } = useAuth()
  const [selectedIntegration, setSelectedIntegration] = useState(null)
  const [clients, setClients] = useState([])
  const [connectionCounts, setConnectionCounts] = useState({})

  useEffect(() => {
    async function load() {
      const { data: c } = await supabase.from('clients').select('id, full_name, business_name').eq('cpa_id', profile.id)
      setClients(c || [])
      const { data: conns } = await supabase.from('client_integrations').select('provider').eq('status', 'connected').in('client_id', (c || []).map(x => x.id))
      const counts = {}
      ;(conns || []).forEach(conn => { counts[conn.provider] = (counts[conn.provider] || 0) + 1 })
      setConnectionCounts(counts)
    }
    if (profile?.id) load()
  }, [profile?.id])

  return (
    <div>
      <div className="card" style={{ padding: '24px 28px', marginBottom: 24, background: '#202326', border: 'none', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 6 }}>Integrations</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>Connect your clients' financial tools to ClearFile for seamless data sync.</p>
          </div>
          <div style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#CBB17B', fontWeight: 600 }}>
            {Object.values(connectionCounts).reduce((a, b) => a + b, 0)} Active Connections
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {INTEGRATIONS.map(integration => (
          <div key={integration.id} className="card animate-fadeUp" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '20px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${integration.color}15`, border: `1px solid ${integration.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{integration.logo}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#202326' }}>{integration.name}</div>
                <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2, color: connectionCounts[integration.id] ? '#10B981' : '#62615D' }}>{connectionCounts[integration.id] ? `● ${connectionCounts[integration.id]} client${connectionCounts[integration.id] > 1 ? 's' : ''} connected` : '○ Not Connected'}</div>
              </div>
              {integration.live ? <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20 }}>LIVE</span> : <span style={{ background: '#F1E8D7', color: '#765B2C', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20 }}>SOON</span>}
            </div>
            <div style={{ padding: '16px 22px' }}>
              <p style={{ fontSize: 13, color: '#62615D', lineHeight: 1.6, marginBottom: 16 }}>{integration.description}</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 18 }}>
                {integration.features.map((f, i) => (<div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#4a5568' }}><span style={{ color: integration.color, fontSize: 10 }}>✓</span>{f}</div>))}
              </div>
              <button onClick={() => setSelectedIntegration(integration)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1.5px solid ${integration.color}`, background: '#fff', color: integration.color, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onMouseEnter={e => { e.currentTarget.style.background = `${integration.color}15` }} onMouseLeave={e => { e.currentTarget.style.background = '#fff' }}>
                {integration.live ? `🔌 Manage ${integration.name}` : `🔜 Coming Soon`}
              </button>
            </div>
          </div>
        ))}
      </div>

      {profile?.role === 'cpa' && <ApiKeysSection userId={profile.id} />}

      {selectedIntegration?.id === 'quickbooks' && <QuickBooksModal clients={clients} onClose={() => setSelectedIntegration(null)} />}
      {selectedIntegration && selectedIntegration.id !== 'quickbooks' && <ComingSoonModal integration={selectedIntegration} onClose={() => setSelectedIntegration(null)} />}
    </div>
  )
}
